import React, { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'

import clsx from 'clsx'

import * as styles from './PlanRoomIndexFrame.module.scss'

import {
  BannerHurusatoNouzeiSmall,
  ButtonWrapper as Button,
  Loader,
  LoginModal,
  MainColumn,
  MultiColumn,
  NotFound,
  SearchLayout,
  SearchMainContent,
  SearchPanel,
  ShowAllToggle,
  SideColumn,
  Sort,
} from '../../components'
import { PlanRoomItem } from '../../components/rooms/PlanRoomItem'
import { PATH } from '../../constants'
import {
  useCreateNextPath,
  usePlanWithRoomsApi,
  usePropertyProvider,
  usePublication,
  useSearchQueryParams,
  useStorageEnabled,
} from '../../hooks'
import i18n from '../../i18n'

const roomIndexNamespace = 'components.RoomIndex'
const planRoomNamespace = 'frames.PlanRoomIndexFrame'
const emptyTranslateNamespace = 'empty.roomNotFound'

/**
 * Landing page for the plan-first flow (`/:property/:plan/rooms`).
 *
 * Backed by the generic `getPlanWithRooms` API (suitebook #30381), which
 * returns the plan for any slug regardless of `isSecret`. The "secret" framing
 * is purely a sharing convention (an `is_secret` plan is reached only by
 * knowing its URL); rendering is NOT gated on `plan.isSecret`.
 *
 * Wrapped in the same 2-column search shell as `RoomIndexFrame`
 * (`SearchLayout` / `MultiColumn` / `SideColumn` / `MainColumn`) so the page is
 * centered (max-width) and responsive on desktop, matching the normal room
 * list.
 *
 * - Surfaces the fixed plan's name as a read-only "プラン" row in the left
 *   SearchPanel (the same form as the property / room-type rows) so users can
 *   identify the plan they landed on. The main results column deliberately
 *   has NO hero plan-description block — it mirrors the normal room list
 *   (result header + room cards) so the two flows look identical.
 * - Always priced: the (default-filled) search params are sent on every load,
 *   and the backend defaults missing dates/counts, so room cards always show a
 *   price + booking CTA (parity with the normal room list and booking).
 * - Sort / show-all / room-scope are applied server-side via
 *   `getPlanWithRooms` (parity with the normal room list).
 * - On a 400 (malformed shared params) or 404 (unknown plan slug) we redirect
 *   to the shared NotFound page. On 5xx we redirect to the server-error page.
 *
 * See docs/specs/2026-05-28-secret-plan-design.md §5.1 / §6.
 */
export const PlanRoomIndexFrame: React.FC = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { property: propertySlug, plan: planSlug } = useParams<{
    property: string
    plan: string
  }>()
  const { params } = useSearchQueryParams()
  const { onCreateNextPath } = useCreateNextPath()
  const { isActivity } = usePropertyProvider()
  const storageEnabled = useStorageEnabled()

  // Always send the (default-filled) search params, exactly like the normal
  // room list (`RoomIndex` passes `...queryParams` unconditionally). The
  // backend prices rooms from defaults whenever it has dates, so a plan-first
  // URL always shows priced rooms — no "select your dates" gate (parity with
  // booking's getPlanRooms, which is always search-driven).
  const searchParams = {
    checkInDate: params.checkInDate,
    checkOutDate: params.checkOutDate,
    roomCount: params.roomCount,
    numberOfAdults: params.numberOfAdults,
    numberOfChildren: params.numberOfChildren,
    numberOfInfants: params.numberOfInfants,
  }

  // Sort / show-all / room-scope are applied server-side (parity with the
  // normal room list — see `RoomIndex`).
  const roomOrder =
    params.roomOrder === 'min_price' || params.roomOrder === '-min_price'
      ? params.roomOrder
      : undefined

  const { data, error, isLoading } = usePlanWithRoomsApi({
    propertySlug: propertySlug ?? '',
    planSlug: planSlug ?? '',
    ...searchParams,
    order: roomOrder,
    showAllRooms: params.showAll,
    // `params.room` is '' on plan URLs without `?room=`; coerce to undefined so
    // the client omits the filter instead of sending `room=` (a blank slug the
    // backend would treat as "filter to nothing"). Mirrors `useRoomsApi`.
    roomSlug: params.room || undefined,
  })

  // Surface the fixed plan's name in the search panel (read-only row),
  // mirroring how the room type name is shown.
  const planPublication = usePublication(i18n.language, data?.plan.publications)

  useEffect(() => {
    const status = (error as { response?: { status?: number } } | undefined)
      ?.response?.status
    // A 400 means the shared URL carried malformed search params (e.g. a
    // hand-edited `adults=abc`): the request can't be served, so route to the
    // same not-found page as an unknown plan rather than leaving the page
    // blank (Codex P2 / FV-4). 404 → not-found; 5xx → server error.
    if (status === 400 || status === 404) {
      navigate(PATH.errors.notFoundError, { replace: true })
    } else if (typeof status === 'number' && status >= 500) {
      navigate(PATH.errors.serverError, { replace: true })
    }
  }, [error, navigate])

  if (!propertySlug || !planSlug) {
    return null
  }

  return (
    <SearchLayout>
      {!storageEnabled && <LoginModal isOpen onClose={() => {}} nextPath="#" />}
      <SearchMainContent>
        <MultiColumn>
          <SideColumn>
            <SearchPanel
              destinationPath={PATH.plan.rooms}
              fillAllSearchKeys
              planName={planPublication?.name}
            />
            <div className={clsx(styles.bannerArea, 'pc')}>
              {isActivity ? null : <BannerHurusatoNouzeiSmall />}
            </div>
          </SideColumn>
          <MainColumn>
            {isLoading ? (
              <Loader />
            ) : (
              <React.Fragment>
                {/* Match the normal room list (RoomIndex): result count +
                    sort + available-only toggle. No custom plan annotation. */}
                <Sort
                  count={data?.availableRooms.length ?? 0}
                  onChange={(e) => {
                    navigate(
                      onCreateNextPath(PATH.plan.rooms, {
                        queryParams: { roomOrder: e.target.value },
                      })
                    )
                  }}
                  defaultValue={params.roomOrder ?? 'min_price'}
                  noMargin
                >
                  <option value="min_price">
                    {t(`${roomIndexNamespace}.Sort.ascPrice`)}
                  </option>
                  <option value="-min_price">
                    {t(`${roomIndexNamespace}.Sort.descPrice`)}
                  </option>
                </Sort>
                <ShowAllToggle />
                {data &&
                  (data.availableRooms.length === 0 ? (
                    // このプランで予約可能な部屋が無いだけ（施設全体が満室とは限らない）
                    // ので、施設横断の満室レコメンドではなくプラン文脈の NotFound を出す。
                    // 同じ施設の他プラン/日付で回復できる導線（条件変更・プラン一覧へ戻る）
                    // を妨げない。
                    <NotFound
                      title={t(`${emptyTranslateNamespace}.attention`)}
                      description1={t(`${emptyTranslateNamespace}.direction`)}
                      description2={t(`${emptyTranslateNamespace}.news`)}
                    />
                  ) : (
                    <ul>
                      {data.availableRooms.map((item) => (
                        <li className={styles.item} key={item.room.slug}>
                          <PlanRoomItem
                            item={item}
                            propertySlug={propertySlug}
                            planSlug={planSlug}
                          />
                        </li>
                      ))}
                    </ul>
                  ))}
              </React.Fragment>
            )}
            <div className={clsx(styles.bannerArea, 'sp')}>
              {isActivity ? null : <BannerHurusatoNouzeiSmall />}
            </div>
          </MainColumn>
        </MultiColumn>
      </SearchMainContent>
      {/* Back to the facility plan list (/:property/plans). Drops the :plan
          segment while keeping the current search params, so the plan list
          reopens with the same dates/guests. Mirrors PlanIndexFrame's
          back-button placement/トンマナ. */}
      <div className={styles.backButton}>
        <Button
          htmlType="button"
          variant="tertiary"
          outline
          size="large"
          position="center"
          onClick={() => navigate(onCreateNextPath(PATH.propertyPlans))}
        >
          {t(`${planRoomNamespace}.backToPlans`)}
        </Button>
      </div>
    </SearchLayout>
  )
}
